import React, { FC, useEffect, useMemo, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import { useAppSelector } from '../../hooks/useAppSelector'
import { checkUnsavedChanges } from '../../store/actions/configurationActions'
import { showModal } from '../../store/actions/modalActions'
import { ModalTypes } from '../../types/modals'
import { createEmptyDocument } from '../models/control'
import { ProductContainer } from '../ProductContainer/ProductContainer'
import { routeConstructor } from './control'
import { IGUIConstructor, ITabData } from './types'
import { User } from '../../entity/User'
import { useAppDispatch } from '../../hooks/useAppDispatch'
import { fetchProductSchemaAndConfig } from '../../store/async-thunks/configurations/fetchProductSchemaAndConfig'
import { configsActions } from '../../store/slices/configsSlice'
import {
    selectConfigsFetchingError,
    selectOneProductConfigSchema,
    selectOneProductConfig,
    selectOneProductSchemaVersion,
} from '../../store/selectors/configSelectors'
import { fetchProductSystemActions } from '../../store/async-thunks/actions/fetchProductSystemActions'
import { fetchProductCustomActions } from '../../store/async-thunks/actions/fetchProductCustomActions'
import { descriptionWindowActions } from '../../store/slices/descriptionWindowSlice'

const GUIConstructor: FC<IGUIConstructor> = ({
    path,
    guid,
    appName,
    product,
}): JSX.Element => {
    const dispatch = useAppDispatch()
    const authUser = new User()
    const fetchingError = useAppSelector(selectConfigsFetchingError)
    const configuration = useAppSelector(selectOneProductConfig(guid)) ?? {}
    const schemaVersion = useAppSelector(selectOneProductSchemaVersion(guid))
    const schema = useAppSelector(selectOneProductConfigSchema(guid)) ?? {}
    const schemaRef = useRef({ schema, schemaVersion })

    if (schemaRef.current.schemaVersion !== schemaVersion) {
        schemaRef.current = { schema, schemaVersion }
    }

    const configurationKeys = Object.keys(configuration)

    for (const item of configurationKeys) {
        if (!Object.keys(configuration[item] || {}).length) {
            const data = createEmptyDocument(schemaRef.current.schema[item], item)
            dispatch(configsActions.initEmptyConfig({ guid, name: item, data })) // NOTE item is not in array
        }
    }

    useEffect(() => {
        if (product.display_name) {
            localStorage.setItem(`${guid}-productName`, product.display_name)
        }
        dispatch(fetchProductSchemaAndConfig({ guid }))
        dispatch(fetchProductSystemActions({ productId: guid }))
        if (authUser.isWlOrAbove()) {
            dispatch(fetchProductCustomActions({ productId: guid }))
        }

        return () => {
            dispatch(checkUnsavedChanges(guid))
            dispatch(descriptionWindowActions.hide())
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        if (fetchingError) {
            dispatch(
                showModal(ModalTypes.ERROR_MODAL, {
                    request: () => fetchProductSchemaAndConfig({ guid }), //fetchProduct(guid),
                    title: <FormattedMessage id="errors.server.request" />,
                    message: (
                        <FormattedMessage id="errors.server.requestMessage" />
                    ),
                })
            )
        }
    }, [fetchingError]) // eslint-disable-line

    const routeData: ITabData[] = useMemo(() => {
        return Object.keys(schemaRef.current.schema).map((el: string) => {
            const keyName = el
            const fileName = el.split('\\')
            const name = fileName[fileName.length - 1].split('.')[0]

            return {
                name: name,
                path: `${path}/${name[0] + name[2]}`,
                component: routeConstructor(
                    schemaRef.current.schema,
                    configuration,
                    el,
                    guid
                ),
                guid,
                keyName,
            }
        })
    }, [schemaRef.current.schema, guid]) // NOTE: configuration is removed from deps

    return (
        <ProductContainer
            productPath={path}
            routesData={routeData}
            appName={appName}
            guid={guid}
        />
    )
}

export default React.memo(GUIConstructor)
